import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import MineIcon from "@/public/assets/images/ShortLogo.png";

interface RingProps {
	delay: number;
	index: number;
}

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Ring: React.FC<RingProps> = ({ delay, index }) => {
	const colors = [
		"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
		"linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
		"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
		"linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
		"linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
		"linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
	];

	return (
		<RingCircle
			$index={index}
			initial={{ scale: 0.8, opacity: 1 }}
			animate={{
				scale: [0.8, 1.1, 1.3],
				opacity: [1, 0.7, 0],
				rotate: [0, 90, 180],
			}}
			transition={{
				delay: delay / 1000,
				duration: 2.5,
				repeat: Infinity,
				repeatType: "loop",
				ease: "easeInOut",
			}}
			$gradient={colors[index % colors.length]}
		/>
	);
};

const ScreenLoader: React.FC = () => {
	return (
		<SafeArea>
			<RingContainer>
				{[...Array(4)].map((_, i) => (
					<Ring key={i} delay={i * 400} index={i} />
				))}
				<LogoContainer>
					<LogoShadow />
					<Logo
						src={MineIcon.src}
						alt="Logo"
						initial={{ scale: 0.9, opacity: 0 }}
						animate={{
							scale: [0.9, 1, 0.9],
							opacity: 1,
						}}
						transition={{
							duration: 1.5,
							repeat: Infinity,
							repeatType: "reverse",
							ease: "easeInOut",
						}}
					/>

				</LogoContainer>

			</RingContainer>
		</SafeArea>
	);
};

export default ScreenLoader;

// Styled Components
const SafeArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  min-width: 100%;
  overflow: hidden;
  position: relative;

  background: #fff;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(-45deg, #6b7280, #3b82f6, #10b981, #f59e0b);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 12s ease infinite;
  opacity: 0.2;
`;

const RingContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RingCircle = styled(motion.div)<{ $gradient: string; $index: number }>`
  position: absolute;
  width: ${props => 180 - props.$index * 20}px;
  height: ${props => 180 - props.$index * 20}px;
  border-radius: 50%;
  border: 4px solid transparent;
  background: ${props => props.$gradient};
  mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  position: relative;
`;

const Logo = styled(motion.img)`
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
`;

const LogoShadow = styled.div`
  position: absolute;
  width: 60px;
  height: 10px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.15);
  bottom: -10px;
  filter: blur(4px);
  z-index: -1;
`;

const LoadingText = styled(motion.span)`
  margin-top: 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: #1f2937;
  letter-spacing: 0.5px;
  font-size: 16px;
`;
